import { z } from "zod";

import { PASSWORD_REGEX } from "@/constants";
import { ERROR_TYPE } from "@/utils";

import { ConnectionScrapingInterval } from "@/enum/connection-scraping-interval.enum";
import { UserLanguage } from "@/enum/user.enum";

import { UpdateUserFormSchema } from "../../Users/schema/update-user";
import { InviteRequestSchema } from "../../Users/schema/invite-user";

const { PASSWORD_GUIDELINES, PASSWORD_MISMATCH, REQUIRED, INVALID_EMAIL } = ERROR_TYPE;

export const UpdateFirstNameSchema = UpdateUserFormSchema.pick({ firstName: true });
export const UpdateLastNameSchema = UpdateUserFormSchema.pick({ lastName: true });
export const UpdateLinkedinUrlSchema = UpdateUserFormSchema.pick({ linkedinUrl: true });
export const UpdateEmailSchema = UpdateUserFormSchema.pick({ email: true });

/** Profile information form: name + LinkedIn URL saved together via one Save button. */
export const UpdateProfileSchema = InviteRequestSchema.pick({
	firstName: true,
	lastName: true,
	linkedinUrl: true,
});

/** Standalone "Change email" modal — email is required and must be a valid address. */
export const ChangeEmailSchema = z.object({
	email: z.string().email(INVALID_EMAIL).trim(),
});

/** Self-service "Edit LinkedIn settings" modal — daily LinkedIn automation limits. */
export const UpdateLinkedinLimitsSchema = z.object({
	visitsPerDay: z.number({ message: REQUIRED }).int().min(0),
	connectionRequestsPerDay: z.number({ message: REQUIRED }).int().min(0),
	messagesPerDay: z.number({ message: REQUIRED }).int().min(0),
});

export const UpdateMeRequestSchema = UpdateUserFormSchema.omit({ email: true, roles: true })
	.extend({
		// Email is editable from the account page via the "Change email" modal, which
		// reuses the same PATCH /users endpoint, so it is allowed back in here as optional.
		email: z.string().email(INVALID_EMAIL).optional(),
		is2FAEnabled: z.boolean().optional(),
		autoAccept: z.boolean().optional(),
		profileVisits: z.boolean().optional(),
		connectionScraping: z.boolean().optional(),
		connectionScrapingInterval: z.nativeEnum(ConnectionScrapingInterval).nullable().optional(),
		autoWithdrawInvitesEnabled: z.boolean().optional(),
		autoWithdrawInvitesDays: z.union([z.literal(30), z.literal(40), z.literal(50)]).optional(),
		// Daily LinkedIn automation limits, editable from the "Edit LinkedIn settings" modal.
		visitsPerDay: z.number().optional(),
		connectionRequestsPerDay: z.number().optional(),
		messagesPerDay: z.number().optional(),
		userLanguage: z.nativeEnum(UserLanguage).optional(),
		country: z.string().nullable().optional(),
		updatedIp: z.string().nullable().optional(),
		tipsShown: z.boolean().optional(),
		isOnboarded: z.boolean().optional(),
	})
	.partial();

export const ChangePasswordSchema = z
	.object({
		oldPassword: z.string().min(1, { message: REQUIRED }).trim(),
		password: z
			.string()
			.min(1, { message: REQUIRED })
			.regex(PASSWORD_REGEX, PASSWORD_GUIDELINES)
			.trim(),
		confirmPassword: z.string().min(1, { message: REQUIRED }).trim(),
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				code: "custom",
				path: ["confirmPassword"],
				message: PASSWORD_MISMATCH,
			});
		}
	});

export const LinkedinConnectionsSchema = z.object({
	file: z.instanceof(File),
});

export const LocationSchema = z.object({
	ip: z.string(),
});

export type UpdateFirstName = z.infer<typeof UpdateFirstNameSchema>;
export type UpdateLastName = z.infer<typeof UpdateLastNameSchema>;
export type UpdateLinkedinUrl = z.infer<typeof UpdateLinkedinUrlSchema>;
export type UpdateEmail = z.infer<typeof UpdateEmailSchema>;
export type UpdateProfile = z.infer<typeof UpdateProfileSchema>;
export type ChangeEmail = z.infer<typeof ChangeEmailSchema>;
export type UpdateLinkedinLimits = z.infer<typeof UpdateLinkedinLimitsSchema>;
export type UpdateMeBody = z.infer<typeof UpdateMeRequestSchema>;
export type ChangePassword = z.infer<typeof ChangePasswordSchema>;
export type LinkedinConnections = z.infer<typeof LinkedinConnectionsSchema>;
export type Location = z.infer<typeof LocationSchema>;
